import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { H2 } from '@liveauctioneers/hammer-ui-core/text';
import { useMemo } from 'react';

import { getGeoAreaType } from '@/pages/AuctionsNearMe/helpers/geoAreaToUrlParams';
import { isPlausibleLocation } from '@/pages/AuctionsNearMe/helpers/isPlausibleLocation';
import { useAppSelector } from '@/redux/hooks';
import { getRelatedGeoAreasSelector } from '@/redux/modules/auctionsNearMe/auctionsNearMe.selectors';
import { RelatedGeoArea } from '@/redux/modules/auctionsNearMe/catalogsNearMe.types';
import { GeoArea } from '@/types/GeoArea';

import { TextTile } from '../RelatedSearches/TextTile';

type Props = {
    geoArea?: GeoArea;
};

export const RelatedGeoAreas = ({ geoArea }: Props) => {
    const geoAreaType = geoArea ? getGeoAreaType(geoArea) : null;
    const { formatMessage } = useIntl();
    // Get related geo areas from catalog-management API (otherPlaces field in the response)
    const relatedGeoAreas = useAppSelector(getRelatedGeoAreasSelector);

    // Drop malformed entries (unrecognized state, duplicated city/state segment) and duplicates.
    // otherPlaces comes from catalog-management's GetOtherNearbyPlaces, which sources raw,
    // unvalidated CMS location text -- this is defense in depth against rendering an internal
    // link to a URL the ANM location-validation loader would itself 404.
    const uniqueRelatedGeoAreas = useMemo(() => {
        return relatedGeoAreas
            .filter((relatedArea: RelatedGeoArea) => isPlausibleLocation(relatedArea.city, relatedArea.state))
            .filter(
                (relatedArea: RelatedGeoArea, index: number, array: RelatedGeoArea[]) =>
                    index ===
                    array.findIndex(
                        (item) =>
                            item.city.toLowerCase() === relatedArea.city.toLowerCase() &&
                            item.state.toLowerCase() === relatedArea.state.toLowerCase()
                    )
            );
    }, [relatedGeoAreas]);

    // Create tiles for related geo areas
    const relatedGeoAreaTiles = useMemo(() => {
        return uniqueRelatedGeoAreas.map((relatedArea: RelatedGeoArea) => {
            let label: string;
            // Show just city when viewing a specific state or city (predefined geo area)
            if (geoAreaType === 'state' || geoAreaType === 'city') {
                label = relatedArea.city;
            } else {
                // Show city, state when viewing country geo area
                // or when no geoArea context (e.g. default bounds or custom search)
                label = `${relatedArea.city}, ${relatedArea.state}`;
            }
            return (
                <TextTile
                    ariaLabel={formatMessage({ id: 'relatedGeoAreaTileAriaLabel' })}
                    isBold
                    key={relatedArea.city}
                    label={label}
                    url={relatedArea.path}
                />
            );
        });
    }, [uniqueRelatedGeoAreas, formatMessage, geoAreaType]);

    return relatedGeoAreaTiles.length > 0 ? (
        <section className="flex flex-col gap-sm">
            <H2>
                <FormattedMessage id="relatedAreasCarouselTitle" />
            </H2>
            <div className="flex flex-wrap items-stretch gap-xs">{relatedGeoAreaTiles}</div>
        </section>
    ) : null;
};
